import {
  MEMBERSHIP_DEMO_URL,
  MEMBERSHIP_PRICING_TIERS,
  MEMBERSHIP_SIGNUP_URL,
} from '../../constants/membershipPricingConstants';

/** Membership application intake (Phase 1). */
export { MEMBERSHIP_SIGNUP_URL, MEMBERSHIP_DEMO_URL };

export const HERO_HEADLINE = 'California Climate Action Portal';

export const HERO_SUBHEADLINE =
  'GHG inventories, indicators, peer benchmarking, engagement tools, and reporting for California local governments.';

export const HERO_TRUST_LINE = "Built by UC Berkeley's CoolClimate Network.";

export const HERO_SEARCH_LABEL = 'Find your city or county';

export const HERO_SEARCH_PLACEHOLDER = 'Search California cities and counties';

export const WORKFLOW_SECTION = {
  title: 'Filling the Gaps to Advance Climate Action',
  subtitle: 'Tools supporting climate planning from assessment through implementation.',
} as const;

export const WORKFLOW_COLUMNS = [
  {
    title: 'Understand',
    items: ['GHG inventories', 'Equity indicators', 'Peer benchmarking', 'Community profiles'],
  },
  {
    title: 'Prioritize',
    items: ['Scenario modeling', 'Policy evaluation', 'Peer comparisons', 'Climate action planning'],
  },
  {
    title: 'Implement',
    items: ['Reports', 'Community engagement', 'Progress tracking', 'Funding support'],
  },
] as const;

export const MEMBERSHIP_HERO = {
  titleLines: ['Spend Less on Climate Planning', 'Invest More in Climate Action'],
  tagline:
    'ClimatePlans is a living climate planning and implementation platform for California local governments.',
  paragraphs: [
    'Our mission is to transform climate planning from a resource-intensive, complicated, and infrequent process into a data-driven cycle of active management and public engagement.',
    'ClimatePlans publishes reviewed GHG inventories, indicators, and planning tools for California communities, helping local governments spend less time assembling data and more time on policy, engagement, funding, and implementation.',
  ],
  goalCallout: {
    label: 'OUR GOAL',
    prefix: 'Help members achieve',
    emphasis: '$10 saved in planning costs for every $1 invested in membership',
    suffix:
      'by reducing repetitive planning work and redirecting staff toward implementation.',
  },
} as const;

export const NEEDS_ASSESSMENT_PDF_URL = '/docs/CoolClimate-Needs-Assessment-2025.pdf';
export const NEEDS_ASSESSMENT_SOURCE_LABEL = 'CoolClimate Network Needs Assessment, 2025';

export const MEMBERSHIP_COST_COMPARISON = {
  intro:
    'Traditional climate planning often requires tens of thousands of dollars for a GHG inventory and hundreds of thousands for a Climate Action Plan. Annual membership is priced at a fraction of the average reported cost of one inventory or CAP and supports recurring data, reporting, benchmarking, engagement, and implementation workflows.',
  disclaimer:
    'These comparisons are not savings guarantees. They compare annual membership prices with average costs reported by responding jurisdictions for one GHG inventory or Climate Action Plan. Actual costs and savings vary. Local tailoring and professional judgment remain essential.',
  columns: [
    'Population',
    'Avg. reported GHG inventory cost',
    'Affiliate membership',
    'Affiliate as % of one inventory',
    'Avg. reported CAP cost',
    'Regular membership',
    'Regular as % of one CAP',
  ],
  rows: MEMBERSHIP_PRICING_TIERS.rows,
} as const;

export type WorkflowStep = {
  icon: string;
  text: string;
};

export const WORKFLOW_COMPARISON = {
  title: 'Two Ways to Do the Work',
  traditional: {
    label: 'Traditional workflow',
    items: [
      { icon: '🔍', text: 'Search for funding opportunities' },
      { icon: '👥', text: 'Hire consultant' },
      { icon: '📁', text: 'Collect data' },
      { icon: '📊', text: 'Produce greenhouse gas inventory' },
      { icon: '📝', text: 'Draft Climate Action Plan' },
      { icon: '📄', text: 'Publish static PDF' },
      { icon: '🔁', text: 'Repeat every few years' },
    ] satisfies WorkflowStep[],
  },
  climatePlans: {
    label: 'ClimatePlans workflow',
    items: [
      { icon: '🗺️', text: 'Reviewed statewide data releases' },
      { icon: '⚡', text: 'Create inventory summaries and exports from published ClimatePlans data' },
      { icon: '📈', text: 'Benchmark against California peers' },
      { icon: '💡', text: 'Identify funding opportunities' },
      { icon: '🤝', text: 'Engage households, businesses, and local governments' },
      { icon: '📋', text: 'Track implementation progress' },
      { icon: '🔄', text: 'Update planning on a reviewed release schedule' },
    ] satisfies WorkflowStep[],
  },
} as const;

export const PUBLIC_ACCESS = {
  tierLabel: 'Public Access',
  heading: 'Explore California Climate Data (Free)',
  description:
    'Explore California communities, compare jurisdictions, and understand local conditions before joining.',
  items: [
    'Explore California communities covered by ClimatePlans',
    'Compare jurisdictions',
    'View indicators and GHG inventories',
    'Learn about membership tools for funding discovery (Regular members, Preview)',
    'Access public dashboards',
  ],
} as const;

export const AFFILIATE_MEMBERSHIP = {
  tierLabel: 'Affiliate Membership',
  heading: 'Inventory, Reporting & Benchmarking',
  priceLabel: 'Starting at $2,500/year',
  intro: 'Inventory summaries, statewide benchmarking, and engagement tools.',
  items: [
    'Inventory summaries and exports from published ClimatePlans data',
    'Data and figure downloads (availability varies by dataset)',
    'Reporting aligned with common inventory protocols',
    'Statewide peer benchmarking where data are published',
    'Member webinars and working groups',
    'Reviewed data release updates',
    'Household and business calculator access',
    'Local Government Workspace (Preview)',
    'CoolClimate Network member recognition',
  ],
} as const;

export const REGULAR_MEMBERSHIP = {
  tierLabel: 'Regular Membership',
  heading: 'Planning & Implementation',
  priceLabel: 'Starting at $5,000/year',
  intro: 'Everything in Affiliate Membership, plus:',
  items: [
    'Climate Planning Workspace (Preview)',
    'Implementation tracking (Preview)',
    'Funding opportunity discovery (Preview)',
    'Policy and measure library (Preview)',
    'Custom dashboards (Preview)',
    'Community engagement tools (Preview)',
    'Organization management (pilot onboarding)',
    'Staff training (pilot)',
    'Technical support (pilot)',
  ],
} as const;

export const WHY_AGENCIES_JOIN = {
  title: 'Why Agencies Join',
  items: [
    { text: 'Reduce recurring planning costs', icon: 'dollar' as const },
    { text: 'Reduce the staff effort required to prepare and review inventory information', icon: 'clock' as const },
    { text: 'Reuse data across reports', icon: 'database' as const },
    { text: 'Discover funding opportunities faster', icon: 'target' as const },
    { text: 'Compare your community with California cities and counties covered by ClimatePlans', icon: 'chart' as const },
    { text: 'Spend more time on implementation', icon: 'building' as const },
  ],
} as const;

export const FEATURED_TOOLS_SECTION = {
  title: 'CoolClimate Member Resources',
  subtitle: 'Analysis, reporting, engagement, and jurisdiction-specific tools.',
} as const;

export type FeaturedToolMembershipLevel = 'free' | 'affiliate' | 'regular';

export const FEATURED_TOOL_MEMBERSHIP_LABELS: Record<FeaturedToolMembershipLevel, string> = {
  free: 'Free Level',
  affiliate: 'Affiliate Level',
  regular: 'Regular Level',
};

export const FEATURED_MEMBER_TOOLS = [
  {
    id: 'climateplans',
    title: 'ClimatePlans Basic',
    description: [
      'Explore more than 100 indicators, with availability varying by community and dataset',
      'Data, figures, and custom narrative',
    ],
    href: '/',
    membershipLevel: 'free',
    isCurrentApp: true,
  },
  {
    id: 'advanced-analysis',
    title: 'Analysis and Reports',
    description: [
      'Statewide analysis and benchmarking (CoolClimate Network members)',
      'Downloadable GHG inventory and data reports',
    ],
    href: '/law-impact',
    membershipLevel: 'affiliate',
    membersOnly: true,
  },
  {
    id: 'local-government-workspace',
    title: 'Local Government Workspace',
    description: [
      'Customize data, charts, and narrative',
      'Add custom data and figures',
      'Local admin privileges',
    ],
    href: '#public-vs-membership',
    membershipLevel: 'affiliate',
  },
  {
    id: 'data-download',
    title: 'Data & Figure Download',
    description: 'Export charts, tables, and datasets for reports.',
    href: '#public-vs-membership',
    membershipLevel: 'affiliate',
  },
  {
    id: 'calculator',
    title: 'CoolClimate Household Calculator',
    description: 'Household engagement calculator (free public page; member licenses for customized campaigns)',
    href: process.env.REACT_APP_CALCULATOR_URL || 'https://coolclimate.org/calculator',
    membershipLevel: 'affiliate',
    external: true,
  },
  {
    id: 'business',
    title: 'CoolClimate Calculator for Businesses',
    description: 'Organizational emissions',
    href: process.env.REACT_APP_BUSINESS_URL || 'https://coolclimate.org/org-calc',
    membershipLevel: 'affiliate',
    external: true,
  },
  {
    id: 'custom-portal',
    title: 'Custom Climate Action Portal',
    description:
      'A customized portal tailored to your jurisdiction\'s goals and workflows.',
    href: '#public-vs-membership',
    membershipLevel: 'regular',
  },
  {
    id: 'consumption-based-ghg-inventory',
    title: 'Consumption-Based GHG Inventory',
    description: [
      'Tract-level CBEI',
      'Sector-level analysis',
      'Local, domestic, and international emissions',
      'Target setting',
    ],
    href: '#public-vs-membership',
    membershipLevel: 'regular',
  },
  {
    id: 'scenario-planning',
    title: 'Scenario Planning',
    description: [
      'Explore GHG mitigation scenarios',
      'Estimate impacts and costs of future actions',
    ],
    href: '#public-vs-membership',
    membershipLevel: 'regular',
  },
  {
    id: 'policy-explorer',
    title: 'Policy Explorer',
    description: [
      'Review ordinances, climate action plans, and general plans (CoolClimate Network members)',
      'Compare policies from peer jurisdictions',
      'Draft with examples and templates',
    ],
    href: '/law-impact',
    membershipLevel: 'regular',
    membersOnly: true,
  },
  {
    id: 'challenge',
    title: 'Cool Campus Challenge',
    description: 'Campus engagement and behavior change',
    href: process.env.REACT_APP_CHALLENGE_URL || 'https://coolcampuschallenge.org/',
    membershipLevel: 'regular',
    external: true,
  },
  {
    id: 'advanced-local-government-admin',
    title: 'Advanced Local Government Admin',
    description: [
      'Household and resident action dashboard',
      'Create and manage custom Challenges',
      'Assign and manage local user content',
      'Messaging for local users',
    ],
    href: '#public-vs-membership',
    membershipLevel: 'regular',
  },
] as const;

export const SUCCESS_STORIES_SECTION = {
  title: 'Success Stories',
  subtitle: 'How California communities use CoolClimate tools.',
} as const;

export const SUCCESS_STORIES = [
  {
    id: 'san-francisco',
    title: 'City and County of San Francisco',
    role: 'Implementation partner',
    excerpt:
      'Industry-leading consumption-based GHG inventory methodology and employee engagement programs.',
    isComingSoon: false,
  },
  {
    id: 'coolcalifornia',
    title: 'CoolCalifornia',
    role: '20+ year statewide partnership',
    excerpt:
      'Partnership with the California Air Resources Board on tools for residents, businesses, schools, and communities.',
    isComingSoon: false,
  },
  {
    id: 'more-stories',
    title: 'More member stories coming',
    role: '',
    excerpt: 'More member stories will be added as organizations join.',
    isComingSoon: true,
  },
] as const;

export const GHG_INVENTORY_SECTION = {
  title: 'Community greenhouse gas inventories for California cities and counties',
  leftColumn: [
    'Sector-based GHG inventories for California cities and counties',
    'Designed to support CEQA greenhouse gas analysis and climate action planning.',
    'Structured to support ICLEI Community Protocol reporting.',
    'Energy data from investor-owned and municipal utilities',
    'Historical estimates back to 2005, where source data are available',
    'Supports CAPs, CEQA analysis, grants, benchmarking, and reporting',
  ],
  rightColumn: [
    'Standardized methodology for statewide peer benchmarking',
    'Built by UC Berkeley with state, local, and academic partners',
    'Consumption-based estimates available at census block-group scale',
    'Updated through reviewed data releases',
    'Design principles: Relevant, Accurate, Consistent, Transparent, Comprehensive, and Cost-Effective',
  ],
} as const;

export const WHY_COOLCLIMATE_SECTION = {
  title: 'Why UC Berkeley\u2019s CoolClimate Network?',
  items: [
    '20+ years developing leading climate research and software for California',
    'Partner on CoolCalifornia.org',
    'Winner of a $1.3M California Legislature grant to develop ClimatePlans.org',
    'Technical advisory board with dozens of experts',
    'Developer of the CoolClimate Calculator with millions of users',
    '10+ years of behavior-based competitions for California residents, universities, and businesses',
  ],
} as const;

export const FINAL_CTA = {
  title: 'Join California\u2019s Climate Planning Network',
  text: 'Cities, counties, campuses, and organizations use CoolClimate tools for inventory, benchmarking, and implementation.',
  trustLine: 'Built by UC Berkeley to support California climate planning.',
} as const;
